import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, CheckCircleOutlined, UserSwitchOutlined, FileSearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useUser } from '../contexts/UserContext';
import './css/searchComponent.css';

const { Search } = Input;
const { Option } = Select;

const isMobile = window.innerWidth <= 768;
const mobileSearchClass = isMobile ? 'custom-search-mobile' : '';

const Customers = () => {
  const user = useUser().user;
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50', '100'] });
  const [searchText, setSearchText] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [gameSettings, setGameSettings] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [instances, setInstances] = useState([]);
  const [originalInstanceName, setOriginalInstanceName] = useState(null);
  const [form] = Form.useForm();
  const [settingsForm] = Form.useForm();
  const [formValues, setFormValues] = useState({});

  const getDayName = (dayNumber) => {
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    return days[dayNumber] || '';
  };

  const calculateCallDay = (gameDay, daysBefore) => {
    if (gameDay === undefined || daysBefore === undefined) return '';
    // Se daysBefore for 0, significa no próprio dia do jogo
    if (daysBefore === 0) return getDayName(gameDay);
    const callDay = (gameDay - daysBefore + 7) % 7;
    return getDayName(callDay);
  };

  const getCallSummary = (gameDay, daysBefore, time) => {
    if ((gameDay !== 0 && !gameDay) || (daysBefore !== 0 && !daysBefore) || !time) return '';
    const callDayName = calculateCallDay(gameDay, daysBefore);
    return `Envio será: ${callDayName} - ${time}`;
  };

  const fetchCustomers = async (page = 1, search = '', pageSize = 5) => {
    setLoading(true);
    try {
      const response = await api.get('/customers', {
        params: { page, limit: pageSize, search }
      });
      setCustomers(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, current: page, pageSize }));
    } catch (error) {
      message.error('Erro ao carregar times');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    api.get('/instances')
      .then((r) => setInstances(Array.isArray(r.data) ? r.data : []))
      .catch(() => setInstances([]));
  }, []);

  const handleTableChange = (pag) => {
    const newPage = pag.pageSize !== pagination.pageSize ? 1 : pag.current;
    fetchCustomers(newPage, searchText, pag.pageSize);
  };

  const fetchGameSettings = async (customerId) => {
    setLoadingSettings(true);
    try {
      const response = await api.get(`/game-settings/customer/${customerId}`);
      setGameSettings(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      setGameSettings([]);
    }
    setLoadingSettings(false);
  };

  const handleRowClick = (record) => {
    setSelectedCustomer(record);
    fetchGameSettings(record.id);
  };

  const showSettingsModal = (setting = null) => {
    setEditingSetting(setting);
    setSettingsModalVisible(true);
    if (setting) {
      settingsForm.setFieldsValue(setting);
      setFormValues(setting);
    } else {
      settingsForm.resetFields();
      setFormValues({});
    }
  };

  const showSummaryModal = (setting) => {
    const summaries = [];
    if ((setting.early_call_at !== undefined && setting.early_call_at !== null) && setting.early_time_at) {
      summaries.push(`1ª Convocação: ${getCallSummary(setting.day_of_week, setting.early_call_at, setting.early_time_at)}`);
    }
    if ((setting.first_call_at !== undefined && setting.first_call_at !== null) && setting.first_time_at) {
      summaries.push(`2ª Convocação: ${getCallSummary(setting.day_of_week, setting.first_call_at, setting.first_time_at)}`);
    }
    if ((setting.second_call_at !== undefined && setting.second_call_at !== null) && setting.second_time_at) {
      summaries.push(`3ª Convocação: ${getCallSummary(setting.day_of_week, setting.second_call_at, setting.second_time_at)}`);
    }
    if ((setting.third_call_at !== undefined && setting.third_call_at !== null) && setting.third_time_at) {
      summaries.push(`4ª Convocação: ${getCallSummary(setting.day_of_week, setting.third_call_at, setting.third_time_at)}`);
    }

    Modal.info({
      title: 'Resumo de Convocações',
      content: (
        <div>
          <p><strong>Jogo:</strong> {getDayName(setting.day_of_week)} - {setting.time}</p>
          {summaries.length > 0 ? (
            summaries.map((summary, index) => (
              <p key={index}>{summary}</p>
            ))
          ) : (
            <p>Nenhuma convocação configurada</p>
          )}
        </div>
      ),
      width: 500
    });
  };

  const handleSettingsSubmit = async (values) => {
    try {
      if (editingSetting) {
        await api.put(`/game-settings/${editingSetting.id}`, values);
        message.success('Configuração atualizada com sucesso!');
      } else {
        await api.post(`/game-settings/customer/${selectedCustomer.id}`, values);
        message.success('Configuração criada com sucesso!');
      }
      setSettingsModalVisible(false);
      fetchGameSettings(selectedCustomer.id);
    } catch (error) {
      message.error('Erro ao salvar configuração');
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCustomers(1, value, 5);
  };

  const showModal = (customer = null) => {
    setEditingCustomer(customer);
    setModalVisible(true);
    if (customer) {
      const isAdminOrSupport = user.profile === 'ADMINISTRADOR' || user.profile === 'SUPORTE';
      if (isAdminOrSupport) {
        // Admin pode editar todos os campos exceto status
        const { status, ...editableData } = customer;
        form.setFieldsValue(editableData);
      } else {
        // Organizador só pode editar 3 campos
        const { game_name, players, goalkeepers } = customer;
        form.setFieldsValue({ game_name, players, goalkeepers });
      }
      setOriginalInstanceName(customer.instance_name || null);
    } else {
      form.resetFields();
      setOriginalInstanceName(null);
    }
  };

  const handleSubmit = async (values) => {
    const newInstanceName = values.instance_name || null;
    const instanceChanged =
      newInstanceName !== originalInstanceName && newInstanceName !== null;

    if (instanceChanged && editingCustomer?.id) {
      try {
        const resp = await api.patch(`/customers/${editingCustomer.id}/instance`, {
          instance_name: newInstanceName,
        });
        const { old_instance, new_instance } = resp.data || {};
        Modal.info({
          title: 'Instância alterada',
          width: 560,
          content: (
            <div>
              <p>
                O organizador precisa adicionar o número <strong>{new_instance?.phone}</strong> como membro do grupo do WhatsApp atual (o grupo continua o mesmo).
              </p>
              {old_instance && (
                <p style={{ color: '#8c8c8c' }}>
                  Número antigo: {old_instance.phone} <em>(pode ser removido do grupo se desejar)</em>
                </p>
              )}
              <Button
                type="default"
                onClick={() => {
                  navigator.clipboard.writeText(new_instance?.phone || '');
                  message.success('Número copiado');
                }}
              >
                Copiar número novo
              </Button>
            </div>
          ),
        });
      } catch (e) {
        message.error(e?.response?.data?.message || 'Erro ao trocar instância');
        return;
      }
    }

    try {
      if (editingCustomer) {
        // Determinar rota baseada no perfil do usuário
        const isAdminOrSupport = user.profile === 'ADMINISTRADOR' || user.profile === 'SUPORTE';
        const endpoint = isAdminOrSupport ?
          `/customers/${editingCustomer.id}/admin-full` :
          `/customers/${editingCustomer.id}/organizer-basic`;

        // Para organizador, enviar apenas 3 campos
        const updateValues = isAdminOrSupport ? values : {
          game_name: values.game_name,
          players: values.players,
          goalkeepers: values.goalkeepers
        };

        await api.put(endpoint, updateValues);
        message.success('Time atualizado com sucesso!');
      } else {
        await api.post('/customers', values);
        message.success('Time criado com sucesso!');
      }
      setModalVisible(false);
      fetchCustomers(pagination.current, searchText);
    } catch (error) {
      message.error('Erro ao salvar time');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      message.success('Time inativado com sucesso!');
      fetchCustomers(pagination.current, searchText);
    } catch (error) {
      message.error('Erro ao inativar time');
    }
  };

  const handleActivate = async (id) => {
    try {
      await api.put(`/customers/${id}/activate`);
      message.success('Time ativado com sucesso!');
      fetchCustomers(pagination.current, searchText);
    } catch (error) {
      message.error('Erro ao ativar time');
    }
  };

  const handleImpersonate = async (customerId, gameName) => {
    Modal.confirm({
      title: 'Acessar como Organizador',
      content: `Deseja acessar o sistema como organizador do time "${gameName}"?`,
      okText: 'Sim',
      cancelText: 'Não',
      onOk: async () => {
        try {
          const response = await api.post('/auth/impersonate', { customerId });

          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));

          message.success(`Agora você está acessando como organizador de ${gameName}`);
          window.location.href = '/team-config';
        } catch (error) {
          message.error('Erro ao acessar como organizador: ' + error.response?.data?.message || 'Verifique os dados do organizador');
        }
      }
    });
  };

  const isAdmin = user?.profile === 'ADMINISTRADOR';
  const canSeeFicha = ['ADMINISTRADOR', 'SUPORTE'].includes(user?.profile);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: (a, b) => a.id - b.id },
    { title: 'Nome do Jogo', dataIndex: 'game_name', key: 'game_name', sorter: (a, b) => a.game_name?.localeCompare(b.game_name) },
    { title: 'Telefone', dataIndex: 'cellphone_number', key: 'cellphone_number', sorter: (a, b) => a.cellphone_number?.localeCompare(b.cellphone_number) },
    { title: 'Dia do Jogo', dataIndex: 'day_of_game', key: 'day_of_game', sorter: (a, b) => a.day_of_game - b.day_of_game },
    { title: 'Jogadores', dataIndex: 'players', key: 'players', sorter: (a, b) => a.players - b.players },
    { title: 'Goleiros', dataIndex: 'goalkeepers', key: 'goalkeepers', sorter: (a, b) => a.goalkeepers - b.goalkeepers },
    { title: 'Status Pagamento', dataIndex: 'payment_status', key: 'payment_status', sorter: (a, b) => a.payment_status?.localeCompare(b.payment_status) },
    { title: 'Status', dataIndex: 'status', key: 'status', sorter: (a, b) => a.status?.localeCompare(b.status) },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canSeeFicha && (
            <Button
              icon={<FileSearchOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/customers/${record.id}/ficha`);
              }}
              size="small"
              type="primary"
              ghost
              title="Ver ficha do time"
            />
          )}
          {isAdmin && (
            <Button
              icon={<UserSwitchOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleImpersonate(record.id, record.game_name);
              }}
              size="small"
              type="default"
              title="Acessar como organizador"
            />
          )}
          <Button
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              showModal(record);
            }}
            size="small"
          />
          {record.status === 'ATIVO' ? (
            <Popconfirm
              title="Tem certeza que deseja inativar este time?"
              onConfirm={(e) => {
                e.stopPropagation();
                handleDelete(record.id);
              }}
            >
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Tem certeza que deseja ativar este time?"
              onConfirm={(e) => {
                e.stopPropagation();
                handleActivate(record.id);
              }}
            >
              <Button
                icon={<CheckCircleOutlined />}
                type="primary"
                size="small"
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0,
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <Search
          className={mobileSearchClass}
          placeholder="Buscar times..."
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={handleSearch}
          style={{ width: isMobile ? '100%' : 300 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()} size={isMobile ? 'large' : 'middle'}>
          Novo Time
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={customers}
        rowKey="id"
        loading={loading}
        pagination={selectedCustomer ? { ...pagination, pageSize: 5 } : pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        showSorterTooltip={false}
        size={selectedCustomer ? 'small' : 'default'}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
      />

      {selectedCustomer && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3>Configurações do Jogo - {selectedCustomer.game_name}</h3>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => showSettingsModal()}>
              Nova Configuração
            </Button>
          </div>
          <Table
            columns={[
              { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
              { title: 'Dia da Semana', dataIndex: 'day_of_week', key: 'day_of_week', render: (value) => getDayName(value) },
              { title: 'Horário', dataIndex: 'time', key: 'time' },
              { title: '1ª Convocação', dataIndex: 'early_call_at', key: 'early_call_at', render: (value, record) => value !== null && value !== undefined ? calculateCallDay(record.day_of_week, value) : '' },
              { title: '1º Horário', dataIndex: 'early_time_at', key: 'early_time_at' },
              { title: '2ª Convocação', dataIndex: 'first_call_at', key: 'first_call_at', render: (value, record) => value !== null && value !== undefined ? calculateCallDay(record.day_of_week, value) : '' },
              { title: '2º Horário', dataIndex: 'first_time_at', key: 'first_time_at' },
              { title: '3ª Convocação', dataIndex: 'second_call_at', key: 'second_call_at', render: (value, record) => value !== null && value !== undefined ? calculateCallDay(record.day_of_week, value) : '' },
              { title: '3º Horário', dataIndex: 'second_time_at', key: 'second_time_at' },
              { title: '4ª Convocação', dataIndex: 'third_call_at', key: 'third_call_at', render: (value, record) => value !== null && value !== undefined ? calculateCallDay(record.day_of_week, value) : '' },
              { title: '4º Horário', dataIndex: 'third_time_at', key: 'third_time_at' },
              { title: 'Número', dataIndex: 'number', key: 'number' },
              { title: 'Status', dataIndex: 'status', key: 'status' },
              { title: 'Tempo Extra', dataIndex: 'early_confirmation_add_extra_time', key: 'early_confirmation_add_extra_time' },
              {
                title: 'Ações',
                key: 'actions',
                render: (_, record) => (
                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => showSettingsModal(record)}
                      size="small"
                    />
                    <Button
                      onClick={() => showSummaryModal(record)}
                      size="small"
                    >
                      Resumo
                    </Button>
                  </Space>
                ),
              },
            ]}
            dataSource={gameSettings}
            rowKey="id"
            loading={loadingSettings}
            pagination={false}
            size="small"
            locale={{ emptyText: 'Nenhuma configuração encontrada' }}
          />
        </div>
      )}

      <Modal
        title={editingCustomer ? 'Editar Time' : 'Novo Time'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={window.innerWidth <= 768 ? '95vw' : 800}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item name="game_name" label="Nome do Jogo" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="players" label="Jogadores" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item name="goalkeepers" label="Goleiros" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>

          {(!editingCustomer || (editingCustomer && (user.profile === 'ADMINISTRADOR' || user.profile === 'SUPORTE'))) && (
            <>
              <Form.Item name="cellphone_number" label="Telefone" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="instance_name" label="Instância WhatsApp">
                <Select
                  showSearch
                  placeholder="Selecione a instância"
                  optionFilterProp="label"
                  allowClear
                  options={instances.map((i) => ({
                    value: i.name,
                    label: `${i.name} (${i.phone || 'sem telefone'}) — ${i.status || '?'} (${i.total_teams ?? 0}/50)`,
                  }))}
                />
              </Form.Item>
              <Form.Item name="day_of_game" label="Dia do Jogo" rules={[{ required: true }]}>
                <Select>
                  <Option value={1}>Segunda</Option>
                  <Option value={2}>Terça</Option>
                  <Option value={3}>Quarta</Option>
                  <Option value={4}>Quinta</Option>
                  <Option value={5}>Sexta</Option>
                  <Option value={6}>Sábado</Option>
                  <Option value={7}>Domingo</Option>
                </Select>
              </Form.Item>
              <Form.Item name="start_game" label="Horário do Jogo">
                <Input />
              </Form.Item>
              <Form.Item name="payment_status" label="Status Pagamento">
                <Select>
                  <Option value="PENDING">Pendente</Option>
                  <Option value="PAID">Pago</Option>
                  <Option value="OVERDUE">Vencido</Option>
                </Select>
              </Form.Item>
              <Form.Item name="trial" label="Trial">
                <Select>
                  <Option value={true}>Sim</Option>
                  <Option value={false}>Não</Option>
                </Select>
              </Form.Item>
              <Form.Item name="early_call" label="1ª Convocação (dias antes do jogo)">
                <Input type="number" />
              </Form.Item>
              <Form.Item name="first_call" label="2ª Convocação (dias antes do jogo)">
                <Input type="number" />
              </Form.Item>
              <Form.Item name="second_call" label="3ª Convocação (dias antes do jogo)">
                <Input type="number" />
              </Form.Item>
              <Form.Item name="third_call" label="4ª Convocação (dias antes do jogo)">
                <Input type="number" />
              </Form.Item>
              <Form.Item name="groupjid" label="Group JID">
                <Input disabled={user.profile !== 'ADMINISTRADOR'} />
              </Form.Item>
              <Form.Item name="plan_id" label="Plano">
                <Select>
                  <Option value={1}>Plano básico</Option>
                  <Option value={2}>Plano Pró</Option>
                </Select>
              </Form.Item>
              <Form.Item name="send_early_notification" label="Enviar Notificação Antecipada">
                <Select>
                  <Option value="SIM">Sim</Option>
                  <Option value="NAO">Não</Option>
                </Select>
              </Form.Item>
              <Form.Item name="custom_game_settings" label="Configurações Personalizadas">
                <Select>
                  <Option value={true}>Sim</Option>
                  <Option value={false}>Não</Option>
                </Select>
              </Form.Item>
              <Form.Item name="open_game_automatically" label="Abrir Jogo Automaticamente">
                <Select>
                  <Option value="SIM">Sim</Option>
                  <Option value="NAO">Não</Option>
                </Select>
              </Form.Item>
              <Form.Item name="private_number" label="Número Privado Bloqueado pelo Whatsapp">
                <Input />
              </Form.Item>
              <Form.Item name="list_description" label="Texto opcional na lista enviada no grupo">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="charge_active" label="Cobrança Ativa">
                <Input disabled value={form.getFieldValue('charge_active') ? 'Sim' : 'Não'} />
              </Form.Item>
              <Form.Item name="first_call_player_type" label="2ª convocação prioritária para:">
                <Select defaultValue={2}>
                  <Option value={1}>Mensalista</Option>
                  <Option value={2}>Avulso</Option>
                </Select>
              </Form.Item>
              <Form.Item name="second_call_player_type" label="3ª convocação prioritária para:">
                <Select defaultValue={2}>
                  <Option value={1}>Mensalista</Option>
                  <Option value={2}>Avulso</Option>
                </Select>
              </Form.Item>
              <Form.Item name="third_call_player_type" label="4ª convocação prioritária para:">
                <Select defaultValue={2}>
                  <Option value={1}>Mensalista</Option>
                  <Option value={2}>Avulso</Option>
                </Select>
              </Form.Item>
            </>
          )}

          {editingCustomer && user.profile !== 'ADMINISTRADOR' && user.profile !== 'SUPORTE' && (
            <div style={{ marginTop: 16, padding: 12, background: '#f0f0f0', borderRadius: 4 }}>
              <p><strong>Nota:</strong> Como organizador, você pode editar apenas Nome do Jogo, Jogadores e Goleiros.</p>
            </div>
          )}

          {editingCustomer && (user.profile === 'ADMINISTRADOR' || user.profile === 'SUPORTE') && (
            <div style={{ marginTop: 16, padding: 12, background: '#e6f7ff', borderRadius: 4 }}>
              <p><strong>Nota:</strong> Como {user.profile.toLowerCase()}, você pode editar todos os campos exceto o status.</p>
            </div>
          )}

          <Form.Item style={{marginTop: 28}}>
            <Space>
              <Button type="primary" htmlType="submit">
                Salvar
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingSetting ? 'Editar Configuração' : 'Nova Configuração'}
        open={settingsModalVisible}
        onCancel={() => setSettingsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={settingsForm} onFinish={handleSettingsSubmit} layout="vertical" onValuesChange={() => setFormValues(settingsForm.getFieldsValue())}>
          <Form.Item name="day_of_week" label="Dia da Semana">
            <Select>
              <Option value={0}>Domingo</Option>
              <Option value={1}>Segunda</Option>
              <Option value={2}>Terça</Option>
              <Option value={3}>Quarta</Option>
              <Option value={4}>Quinta</Option>
              <Option value={5}>Sexta</Option>
              <Option value={6}>Sábado</Option>
            </Select>
          </Form.Item>
          <Form.Item name="time" label="Horário">
            <Input placeholder="Ex: 20:00" />
          </Form.Item>
          <Form.Item name="early_call_at" label="1ª Convocação (dias)">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="early_time_at" label="1º Horário">
            <Input placeholder="Ex: 18:00" />
          </Form.Item>
          {formValues.day_of_week !== undefined && (formValues.early_call_at !== undefined && formValues.early_call_at !== null) && formValues.early_time_at && (
            <div style={{ marginBottom: 16, padding: 8, background: '#f0f0f0', borderRadius: 4, fontSize: 12 }}>
              {getCallSummary(formValues.day_of_week, formValues.early_call_at, formValues.early_time_at)}
            </div>
          )}
          <Form.Item name="first_call_at" label="2ª Convocação (dias)">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="first_time_at" label="2º Horário">
            <Input placeholder="Ex: 19:00" />
          </Form.Item>
          {formValues.day_of_week !== undefined && (formValues.first_call_at !== undefined && formValues.first_call_at !== null) && formValues.first_time_at && (
            <div style={{ marginBottom: 16, padding: 8, background: '#f0f0f0', borderRadius: 4, fontSize: 12 }}>
              {getCallSummary(formValues.day_of_week, formValues.first_call_at, formValues.first_time_at)}
            </div>
          )}
          <Form.Item name="second_call_at" label="3ª Convocação (dias)">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="second_time_at" label="3º Horário">
            <Input placeholder="Ex: 19:30" />
          </Form.Item>
          {formValues.day_of_week !== undefined && (formValues.second_call_at !== undefined && formValues.second_call_at !== null) && formValues.second_time_at && (
            <div style={{ marginBottom: 16, padding: 8, background: '#f0f0f0', borderRadius: 4, fontSize: 12 }}>
              {getCallSummary(formValues.day_of_week, formValues.second_call_at, formValues.second_time_at)}
            </div>
          )}
          <Form.Item name="third_call_at" label="4ª Convocação (dias)">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="third_time_at" label="4º Horário">
            <Input placeholder="Ex: 19:45" />
          </Form.Item>
          {formValues.day_of_week !== undefined && (formValues.third_call_at !== undefined && formValues.third_call_at !== null) && formValues.third_time_at && (
            <div style={{ marginBottom: 16, padding: 8, background: '#f0f0f0', borderRadius: 4, fontSize: 12 }}>
              {getCallSummary(formValues.day_of_week, formValues.third_call_at, formValues.third_time_at)}
            </div>
          )}
          <Form.Item name="number" label="Número">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select>
              <Option value="ATIVO">Ativo</Option>
              <Option value="INATIVO">Inativo</Option>
            </Select>
          </Form.Item>
          <Form.Item name="early_confirmation_add_extra_time" label="Tempo Extra">
            <Input type="number" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Salvar
              </Button>
              <Button onClick={() => setSettingsModalVisible(false)}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Customers;